import { ChangeDetectorRef, Directive, inject, OnInit } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { ConfirmationService } from "primeng/api";
import { CrudService } from "src/app/services/crud.service";

@Directive()
export abstract class AbstractListController<T, ID> implements OnInit {
  items: T[] = [];
  filteredItems: T[] = [];
  item: T;
  page = 1;
  count = 1;
  pageSize = 10;
  pageSizes = [5, 10, 15];
  loading = false;
  error: string;
  cols: any[] = [];
  colsFilter: any[] = [];
  columnOptions: any[] = [];
  selectedColumns: any[] = [];
  filterSidebar: boolean = false;
  [key: string]: any;

  /** When true, filtering is driven by the generic DataTableComponent. */
  protected useTableFilters = false;
  tableFilters: any[] = [];
  tableFilterValues: Record<string, any> = {};

  private initialized = false;
  protected cdr = inject(ChangeDetectorRef);

  private loadingRequest = false;

  constructor(
    protected service: CrudService<T, ID>,
    protected confirmationService: ConfirmationService,
    protected router: Router,
    protected route: ActivatedRoute,
    public path: string,
  ) {}

  ngOnInit(): void {
    this.initializeFromUrl();
  }

  clearFilters(): void {
    this.cols.forEach((col) => {
      const filterProp = col.field + "Filter";
      this[filterProp] = "";
    });
  }

  reset(): void {
    this.clearFilters();
    this.page = 1;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        page: this.page,
        limit: this.pageSize,
      },
    });

    this.loadItems();
  }

  load(): void {
    if (this.loadingRequest) return;
    this.loadingRequest = true;

    const params = this.getRequestParams(this.page, this.pageSize);

    this.service.list(params).subscribe({
      next: (data) => {
        this.items = data;
        this.count = this.service.size;
        this.applyFilters(this.getFilterParams());
        this.loading = false;
        this.loadingRequest = false;
        this.afterLoad();
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.error = err;
        this.loading = false;
        this.loadingRequest = false;
      },
    });

    this.updateUrlParams(params);
  }

  protected afterLoad(): void {}

  setupDynamicFilters(): void {
    this.columnOptions = this.cols.map((col) => ({
      label: col.header,
      value: col,
    }));

    this.selectedColumns = [...this.cols];

    this.cols.forEach((col) => {
      const filterProp = col.field + "Filter";
      if (this[filterProp] === undefined) {
        this[filterProp] = "";
      }
    });
  }

  getFilterLabel(col: any): string {
    const val = this[col.field + "Filter"];

    if (col.type === "select") {
      const option = col.options?.find((opt: any) => opt.value === val);
      return option ? option.label : val;
    }

    return val;
  }

  clearSingleFilter(col: any): void {
    const filterKey = col.field + "Filter";

    this[filterKey] = "";
    this.page = 1;

    const queryParams: any = {
      _start: this.page,
      _limit: this.pageSize,
      ...this.getFilterParams(),
    };

    delete queryParams[col.field];

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
    });

    this.loadItems();
  }

  onPrimaryFilterChange(): void {
    this.page = 1;

    const filters = this.getFilterParams();

    if (Object.values(filters).some((val) => val === null)) {
      this.reset();
    }

    const cleanedFilters = Object.fromEntries(
      Object.entries(filters).filter(
        ([, val]) => val !== null && val !== undefined && val !== "",
      ),
    );

    const queryParams = {
      _start: this.page,
      _limit: this.pageSize,
      ...cleanedFilters,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: undefined,
    });

    this.updateUrlParams(queryParams);
    this.loadItems();
  }

  filterItem(item: T, filters: any): boolean {
    return this.cols.every((col) => {
      const key = col.field as keyof T;
      const itemValue = item[key];
      const filterValue = filters[col.field];

      if (!filterValue) return true;

      if (typeof itemValue === "string") {
        return itemValue.toLowerCase().includes(filterValue.toLowerCase());
      }

      if (typeof itemValue === "number") {
        return itemValue === +filterValue;
      }

      if (itemValue instanceof Date || typeof itemValue === "object") {
        return JSON.stringify(itemValue)
          .toLowerCase()
          .includes(filterValue.toString().toLowerCase());
      }

      return true;
    });
  }

  loadItems(): void {
    if (this.loadingRequest) return;
    this.loadingRequest = true;

    const params = this.getRequestParams(this.page, this.pageSize);

    this.service.list(params).subscribe({
      next: (data) => {
        // The current page can disappear after a delete (e.g. the last row of the
        // last page was removed): step back to the new last page and reload.
        const lastPage = Math.max(1, Math.ceil(this.service.size / this.pageSize));
        if (this.page > lastPage) {
          this.page = lastPage;
          this.loadingRequest = false;
          this.loadItems();
          return;
        }

        this.items = data;
        this.count = this.service.size;
        this.applyFilters(this.getFilterParams());
        this.loading = false;
        this.loadingRequest = false;
        this.afterLoad();
        this.cdr.detectChanges();

        this.updateUrlParams(params);
      },
      error: (err) => {
        this.error = err;
        this.loading = false;
        this.loadingRequest = false;
      },
    });
  }

  handlePageChange(event: number): void {
    this.page = event;
    this.loadItems();
  }

  handlePageSizeChange(event: any): void {
    this.pageSize = event.target.value;
    this.page = 1;
    this.loadItems();
  }

  getFilterParams(): any {
    if (this.useTableFilters) {
      const params: any = {};
      Object.entries(this.tableFilterValues || {}).forEach(([key, val]) => {
        if (val !== null && val !== undefined && val !== "") {
          params[key] = val;
        }
      });
      return params;
    }

    const params: any = {};
    this.cols.forEach((col) => {
      const filterProp = col.field + "Filter";
      if (this[filterProp]) {
        params[col.field] = this[filterProp];
      }
    });
    return params;
  }

  applyFilterParams(params: any): void {
    if (this.useTableFilters) {
      const seed: Record<string, any> = {};
      this.tableFilters.forEach((f) => {
        if (params[f.field] !== undefined && params[f.field] !== "") {
          seed[f.field] = params[f.field];
        }
      });
      this.tableFilterValues = seed;
      return;
    }

    this.cols.forEach((col) => {
      const filterProp = col.field + "Filter";
      this[filterProp] = params[col.field] || "";
    });
  }

  onTableSearch(values: Record<string, any>): void {
    this.tableFilterValues = values;
    this.page = 1;
    this.loadItems();
  }

  getItemByField(fieldName: string, value: any, collection: any[]): any {
    return collection.find((item) => item[fieldName] === value);
  }

  resetFilters(): void {
    this.clearFilters();
    this.page = 1;
    this.loadItems();
  }

  deleteItem(id: number): void {
    this.confirmationService.confirm({
      message: "Sei sicuro di voler eliminare questo elemento?",
      header: "Conferma eliminazione",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        this.service.delete(id).subscribe({
          next: () => this.loadItems(),
          error: (err) => (this.error = err),
        });
      },
    });
  }

  onDelete(id: number): void {
    this.confirmationService.confirm({
      message: "Sei sicuro di voler eliminare questo elemento?",
      header: "Conferma eliminazione",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        this.service.delete(id).subscribe({
          next: () => this.loadItems(),
          error: (err) => (this.error = err),
        });
      },
    });
  }

  protected applyFilters(filters: any): void {
    this.filteredItems = this.items.filter((item) =>
      this.filterItem(item, filters),
    );
    this.paginate();
  }

  protected paginate(): void {
    this.filteredItems = this.filteredItems.slice(
      (this.page - 1) * this.pageSize,
      this.page * this.pageSize,
    );
  }

  protected getRequestParams(page: number, pageSize: number): any {
    return {
      _start: page,
      _limit: pageSize,
      ...this.getFilterParams(),
    };
  }

  protected updateUrlParams(params: any): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        _start: this.page,
        _limit: this.pageSize,
        ...this.getFilterParams(),
      },
      queryParamsHandling: "merge",
    });
  }

  protected initializeFromUrl(): void {
    this.route.queryParams.subscribe((params) => {
      this.page = +params["_start"] || 1;
      this.pageSize = +params["_limit"] || 10;
      this.applyFilterParams(params);

      if (!this.initialized) {
        this.initialized = true;
        this.loadItems();
      }
    });
  }
}
